import { useMutation } from '@tanstack/react-query';
import { getUploadErrorMessage, UploadFileVariables } from '../../../../upload';
import { uploadPreTradeCheckerFile } from '../pre-trade-checker.routes';
import { PreTradeCheckerBatchUploadResponse } from '../types';
import { toast } from '../../../../../components/toast/Toast';

export function getPreTradeUploadResultErrorMessage(
  data: PreTradeCheckerBatchUploadResponse,
): string | null {
  const succeededCount = Object.keys(data.succeeded ?? {}).length;
  if (succeededCount > 0) {
    return null;
  }

  const checkErrorCount = Object.keys(data.check_errors ?? {}).length;
  const parsingErrorCount = Object.keys(data.parsing_errors ?? {}).length;
  const failedCount = checkErrorCount + parsingErrorCount;

  if (failedCount === 0) {
    return 'No ISINs could be processed from this file.';
  }

  const firstParsingError = Object.values(data.parsing_errors ?? {})[0];
  if (firstParsingError?.reason) {
    return firstParsingError.reason;
  }

  const firstCheckError = Object.values(data.check_errors ?? {})[0];
  if (typeof firstCheckError === 'string') {
    return firstCheckError;
  }
  if (firstCheckError?.reason) {
    return firstCheckError.reason;
  }

  return `${failedCount} ISIN(s) could not be processed from this file.`;
}

function showPreTradeUploadResultToast(
  data: PreTradeCheckerBatchUploadResponse,
) {
  const succeededCount = Object.keys(data.succeeded ?? {}).length;
  const checkErrorCount = Object.keys(data.check_errors ?? {}).length;
  const parsingErrorCount = Object.keys(data.parsing_errors ?? {}).length;
  const failedCount = checkErrorCount + parsingErrorCount;

  if (data.full_success && failedCount === 0) {
    toast({
      title: 'Upload successful',
      message: `${succeededCount} ISIN(s) checked successfully.`,
      type: 'success',
    });
    return;
  }

  if (succeededCount > 0) {
    toast({
      title: 'Partial upload',
      message: `${succeededCount} ISIN(s) checked, ${failedCount} could not be processed.`,
      type: 'warning',
    });
  }
}

type PreTradeCheckerUploadMutationOptions = {
  /** When false, HTTP errors are not toasted (e.g. shown in the upload modal instead). */
  showErrorToast?: boolean;
};

export function usePreTradeCheckerUploadMutation(
  fundId: string,
  positionDate: string,
  options: PreTradeCheckerUploadMutationOptions = {},
) {
  const { showErrorToast = true } = options;

  return useMutation({
    mutationKey: ['pre-trade-checker-upload', fundId, positionDate],
    mutationFn: (variables: UploadFileVariables) =>
      uploadPreTradeCheckerFile({ fundId, positionDate, ...variables }),
    onSuccess: (response) => {
      if (getPreTradeUploadResultErrorMessage(response.data)) {
        return;
      }

      showPreTradeUploadResultToast(response.data);
    },
    onError: (error) => {
      if (!showErrorToast) {
        return;
      }

      toast({
        title: 'Upload failed',
        message: getUploadErrorMessage(error),
        type: 'error',
      });
    },
  });
}
